import React from 'react';
import { createRoot } from 'react-dom/client';
import {
  PublicClientApplication,
  EventType,
  AuthenticationResult,
  AccountInfo,
} from '@azure/msal-browser';
import webStorage, { LOCAL_STORAGE_KEYS } from './core/web-storage/webStorage';
import { xorEncryptDecrypt } from '@/common/utils/encryption.utils';
import {
  decodeJWT,
  getBoardFromJWT,
  getUserEmailFromJWT,
  getUserIdFromJWT,
  getUserNameFromJWT,
  getUserPortalRoleFromJWT,
} from '@/common/utils/app.utils';
import ErrorBoundaryRoot from '@/components/Error/ErrorBoundaryRoot';
import AppProviders from '@/root-app/AppProviders';
import { appConfig, msalConfig } from '@/authentication/authenticationConfig';
import MinimalAppWithError from '@/root-app/MinimalAppWithError';
import tokens from '@/core/tokens.json';
import { PORTAL_ROLES } from '@/authorization/authorization.constants';
import ApplicationInsightsProvider from '@/common/ApplicationInsights/ApplicationInsightsProvider';
import AppErrorBoundary from '@/components/Error/AppErrorBoundary';

const rootContainer = document.getElementById('root') as HTMLElement;
const msalInstance = new PublicClientApplication(msalConfig);

msalInstance
  .initialize()
  .then(() => {
    if (!msalInstance.getActiveAccount() && msalInstance.getAllAccounts().length > 0) {
      msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
    }

    const root = createRoot(rootContainer);
    msalInstance.enableAccountStorageEvents();
    msalInstance.addEventCallback((event) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        const account = payload.account;
        msalInstance.setActiveAccount(account);
        webStorage.local.removeItems(Object.values(LOCAL_STORAGE_KEYS));

        const idToken = payload.idToken;
        if (idToken) {
          const decodedToken = decodeJWT(idToken);
          const boardId = getBoardFromJWT(decodedToken);
          const userId = getUserIdFromJWT(decodedToken);
          const userPortalRole = getUserPortalRoleFromJWT(decodedToken);
          const userName = getUserNameFromJWT(decodedToken);
          const userEmail = getUserEmailFromJWT(decodedToken);
          if (
            boardId &&
            userPortalRole !== PORTAL_ROLES.ATRF &&
            userPortalRole !== PORTAL_ROLES.ATA
          ) {
            webStorage.local.setItem(LOCAL_STORAGE_KEYS.BOARD_ID, boardId);
          }
          if (userId) {
            webStorage.local.setItem(LOCAL_STORAGE_KEYS.USER_ID, userId);
          }
          if (userPortalRole) {
            const encryptedPortalRole = xorEncryptDecrypt(userPortalRole);
            webStorage.local.setItem(LOCAL_STORAGE_KEYS.PORTAL_ROLE, encryptedPortalRole);
          }
          if (userName) {
            webStorage.local.setItem(LOCAL_STORAGE_KEYS.USER_NAME, userName);
          }
          if (userEmail) {
            webStorage.local.setItem(LOCAL_STORAGE_KEYS.USER_EMAIL, userEmail);
          }
        }
      }
      if (event.eventType === EventType.LOGIN_FAILURE) {
        void msalInstance.logoutRedirect();
      }
      if (event.eventType === EventType.ACCOUNT_REMOVED) {
        void msalInstance.logoutRedirect();
      }
      if (event.eventType === EventType.LOGOUT_START) {
        webStorage.local.removeItems(Object.values(LOCAL_STORAGE_KEYS));
      }
    });
    root.render(
      <React.StrictMode>
        <ApplicationInsightsProvider>
          <AppErrorBoundary FallbackComponent={ErrorBoundaryRoot}>
            <AppProviders pca={msalInstance} />
          </AppErrorBoundary>
        </ApplicationInsightsProvider>
      </React.StrictMode>,
    );
  })
  .catch(() => {
    const root = createRoot(rootContainer);
    root.render(
      <ApplicationInsightsProvider>
        <MinimalAppWithError title={tokens.WRONG} description={tokens.TRY_AGAIN} />
      </ApplicationInsightsProvider>,
    );
  });

export async function getAccessToken() {
  if (!msalInstance) {
    throw new Error('msal instance not available');
  }
  await msalInstance.handleRedirectPromise();
  const activeAccount = msalInstance.getActiveAccount();
  if (!activeAccount) {
    throw new Error('No active account!');
  }

  try {
    const response = await msalInstance.acquireTokenSilent({
      scopes: appConfig.scopes,
      account: activeAccount as AccountInfo,
    });
    return response.accessToken;
  } catch (e) {
    void msalInstance.logoutRedirect();
  }
}
